import {
  type FilingMunicipality,
  REGISTRATION_SYSTEMS,
  type RegistrationSystem,
  type RegistrationSystemCategory,
  systemConfigAt,
} from "./registration-systems";
import { STATISTICS_REGIONS } from "./statistics-filing";

/**
 * What Vidimo does about each compliance regime, and whether we may say so.
 * Every surface reads this before it makes a filing claim: the marketing regime
 * pages, the app's signup, the comparison hub and the CRM outreach drafts.
 *
 * Three facts, kept apart because they disagree:
 *
 * 1. `automation` — what machinery exists. A fact about this repository.
 * 2. `claim` — whether it serves customers. A commercial fact, never derived
 *    from (1): a connector registered against the engine builds a report for
 *    every guest, and its filing may not be claimed until a `provenBy` records
 *    what proved it.
 * 3. `obligation` — which duty the regime discharges. A country holds several,
 *    and each can go live on its own.
 *
 * So there is no answer at country grain, and there must not be: a boolean per
 * country has to AND that country's regimes together, so the first of Italy's
 * three to go live would read as waiting. Ask
 * {@link regimeForCountryObligation} for the regime, then ask the regime one of
 * two questions:
 *
 * - {@link regimeServesCustomers} — may a page claim what it claims? What is
 *   claimed differs per regime: the tourist-tax page claims a calculation.
 * - {@link regimeFilesToAuthority} — does Vidimo transmit this to the
 *   authority? True only where a registered connector serves customers, so a
 *   live tourist tax still answers false: Vidimo calculates it, the host
 *   remits it.
 *
 * Reading the wrong one is the failure mode, so ask through a named function.
 * {@link REGIMES} is exported for the tests that hold it honest; `claim.live`
 * on a row is the narrower fact, and a surface meaning "do we file this" that
 * reads it gets the wrong answer.
 *
 * An unknown slug or an unmodelled country answers no, the way
 * `packages/crm-core/src/contactability.ts` does: a market nobody has modelled
 * must never inherit a promise from one that has.
 */

/** The duty a regime discharges. A country holds several, independently. */
export type RegimeObligation =
  | "guest-registration"
  | "property-registration"
  | "statistics"
  | "tourist-tax";

/** The duties a registration system's filings can discharge. */
export type FiledObligation = Exclude<
  RegimeObligation,
  "property-registration"
>;

/**
 * The obligation a registration system's category serves. The credential
 * catalog keys systems by `category` and this table keys regimes by the wider
 * `obligation` (a tourist tax and a property declaration have no system at
 * all), so the two vocabularies meet here rather than in an assertion. Adding
 * a category without deciding its obligation fails to compile.
 */
export const OBLIGATION_BY_SYSTEM_CATEGORY: Record<
  RegistrationSystemCategory,
  FiledObligation
> = {
  guest_registration: "guest-registration",
  statistics: "statistics",
};

/** Every obligation a system's filings discharge, its category's first. */
export function obligationsFiledBy(
  system: RegistrationSystem
): FiledObligation[] {
  const { alsoFiles, category } = REGISTRATION_SYSTEMS[system];
  return [OBLIGATION_BY_SYSTEM_CATEGORY[category], ...alsoFiles];
}

/**
 * The machinery that exists for a regime, independent of whether we sell it.
 *
 * `filing-connector` names a system in `REGISTRATION_SYSTEMS` that
 * `registerFilingConnectors()` wires into the engine;
 * `apps/api/src/services/filing/connector-claims.test.ts` fails when this and
 * that registration disagree. `in-product` is computed inside Vidimo with
 * nothing sent to an authority (the tourist-tax engine). `none` is no code.
 */
export type RegimeAutomation =
  | { kind: "filing-connector"; system: RegistrationSystem }
  | { kind: "in-product" }
  | { kind: "none" };

/**
 * Whether the regime serves customers, and on what evidence.
 *
 * `live` is not a boolean anyone can flip: it carries the date the claim became
 * true and the sentence saying what made it true. Setting it means writing down
 * what was proven, to the bar AGENTS.md sets in "Never wait on production
 * proof". No test here can know that, so this does not make the claim safe —
 * it makes an unevidenced claim impossible to add by accident.
 *
 * A waiting regime's exception, the whole sentence signup shows a host who
 * arrives from that regime's page, is the host catalog's
 * `labels.regimeException.<slug>`, so it reads in the host's language; a live
 * regime keeps its entry for the day its claim goes back to waiting. It names
 * every filing that page says Vidimo does not do yet, and what Vidimo already
 * prepares where the page leads with it. It never tells the host to file
 * anything themselves, and its tense follows `automation`: a registered
 * connector reads "still in preparation" (in each language, the word its
 * catalog's status badge gives that state), because it files once its claim
 * goes live, and a host told at signup that Vidimo cannot file would keep
 * filing by hand and every guest would be filed twice.
 * No connector's own filing reads "can't … yet", or a plain "doesn't" where the page never
 * promised the filing at all (the French page).
 *
 * Going live also owes a migration setting `connected_at` to the go-live on
 * that system's active credentials at properties that are not demos: "Connect
 * to check" stamps it earlier, and filing starts at stays arriving from it.
 */
export type RegimeClaim =
  | { live: false }
  | { live: true; provenBy: string; since: string };

export interface RegimeRecord {
  automation: RegimeAutomation;
  claim: RegimeClaim;
  /** ISO 3166-1 alpha-2. Country is data here, never structure. */
  countryCode: string;
  obligation: RegimeObligation;
}

export const complianceRegimeSlugValues = [
  "alloggiati-web",
  "declaration-meuble-de-tourisme",
  "eturizem",
  "evisitor",
  "imposta-di-soggiorno",
  "istat-ross-1000",
  "ses-hospedajes",
  "siba",
] as const;

export type ComplianceRegimeSlug = (typeof complianceRegimeSlugValues)[number];

/**
 * Every regime Vidimo publishes a page for, keyed by that page's slug
 * (`/siba`, `/eturizem`, ...). Prefer the named functions below: they say which
 * of the three facts they answer.
 */
export const REGIMES: Record<ComplianceRegimeSlug, RegimeRecord> = {
  "alloggiati-web": {
    automation: { kind: "filing-connector", system: "alloggiati_web" },
    claim: {
      live: true,
      provenBy:
        "The connector's gated live smoke test against the real service is merged (#1513), and the schedina wire format was checked field by field against the Polizia di Stato's WS_ALLOGGIATI manual, Rev. 01 (#1653). The smoke test has never run: no pilot account has passed the service's Test validation, the bar AGENTS.md (\"Never wait on production proof\") sets for an authority with no test environment. On 2026-10-12 the owner overrode that bar and decided this evidence proves it.",
      since: "2026-10-12",
    },
    countryCode: "IT",
    obligation: "guest-registration",
  },
  "declaration-meuble-de-tourisme": {
    automation: { kind: "none" },
    claim: { live: false },
    countryCode: "FR",
    obligation: "property-registration",
  },
  eturizem: {
    automation: { kind: "filing-connector", system: "eturizem" },
    claim: {
      live: true,
      provenBy:
        "AJPES's test server (wwwt.ajpes.si) accepted Vidimo's arrival, departure and storno packages for one facility end to end, each with a persisted receipt (SUP-1019). No production filing preceded the claim.",
      since: "2026-09-23",
    },
    countryCode: "SI",
    obligation: "guest-registration",
  },
  evisitor: {
    automation: { kind: "none" },
    claim: { live: false },
    countryCode: "HR",
    obligation: "guest-registration",
  },
  "imposta-di-soggiorno": {
    // The tax engine, not a connector: nothing is transmitted, so once live
    // this regime serves customers and still answers false to
    // `regimeFilesToAuthority`. A stay's tax shows only once the integration
    // that files it is set up, and no Italian integration files it yet, so
    // rates in packages/compliance/data/it alone do not make this live.
    automation: { kind: "in-product" },
    claim: { live: false },
    countryCode: "IT",
    obligation: "tourist-tax",
  },
  "istat-ross-1000": {
    automation: { kind: "none" },
    claim: { live: false },
    countryCode: "IT",
    obligation: "statistics",
  },
  "ses-hospedajes": {
    automation: { kind: "none" },
    claim: { live: false },
    countryCode: "ES",
    obligation: "guest-registration",
  },
  siba: {
    automation: { kind: "none" },
    claim: { live: false },
    countryCode: "PT",
    obligation: "guest-registration",
  },
};

/** The signup query parameter a trial link carries the regime slug in. */
export const SIGNUP_REGIME_PARAM = "regime";

export function isComplianceRegimeSlug(
  value: string
): value is ComplianceRegimeSlug {
  return (complianceRegimeSlugValues as readonly string[]).includes(value);
}

function regimeRecord(slug: string | null | undefined): RegimeRecord | null {
  if (!(slug && isComplianceRegimeSlug(slug))) {
    return null;
  }
  return REGIMES[slug];
}

/**
 * Whether this regime's page may claim what it claims — the gate the marketing
 * pages read as `connectorLive` and signup reads to decide whether it owes the
 * host an exception.
 *
 * What is claimed differs per regime, so this is NOT "Vidimo files this for
 * you": a tourist tax serves customers by calculating. Ask
 * {@link regimeFilesToAuthority} for the filing.
 */
export function regimeServesCustomers(
  slug: ComplianceRegimeSlug | null | undefined
): boolean {
  return regimeRecord(slug)?.claim.live ?? false;
}

/**
 * Whether Vidimo transmits this regime's filing to its authority on the host's
 * behalf. The question outreach and the comparison hub must ask before writing
 * "we send it for you".
 *
 * Both halves are required: a connector can exist before its claim goes live,
 * and a regime that serves customers without one is a tourist tax, which
 * Vidimo calculates and the host remits.
 */
export function regimeFilesToAuthority(
  slug: ComplianceRegimeSlug | null | undefined
): boolean {
  const record = regimeRecord(slug);
  return (
    record?.claim.live === true && record.automation.kind === "filing-connector"
  );
}

/**
 * Does Vidimo build this regime's filing from the booking, whether or not it
 * may yet send it?
 *
 * A registered connector prepares every report the moment a host connects
 * credentials; {@link regimeFilesToAuthority} is the narrower question of
 * whether the sending may be claimed. The outreach drafts ask this one,
 * because what they offer a host who files by hand is the preparing, and a
 * market with no connector has nothing prepared to offer. An unknown slug
 * answers no.
 */
export function regimePreparesFiling(
  slug: ComplianceRegimeSlug | null | undefined
): boolean {
  return regimeRecord(slug)?.automation.kind === "filing-connector";
}

/**
 * Every regime published for a country, in catalog order. Empty for an
 * unmodelled country, which is the closed default: a market with no row here
 * gets no claim from one that has.
 */
export function regimesForCountry(
  countryCode: string | null | undefined
): ComplianceRegimeSlug[] {
  if (!countryCode) {
    return [];
  }
  const upper = countryCode.toUpperCase();
  return complianceRegimeSlugValues.filter(
    (slug) => REGIMES[slug].countryCode === upper
  );
}

/**
 * The regime discharging one obligation in one country, or null where the
 * country has no page for it. This is how a surface asks a country-shaped
 * question without getting a country-shaped answer: it resolves to a regime,
 * and the regime carries the state.
 */
export function regimeForCountryObligation(
  countryCode: string | null | undefined,
  obligation: RegimeObligation
): ComplianceRegimeSlug | null {
  return (
    regimesForCountry(countryCode).find(
      (slug) => REGIMES[slug].obligation === obligation
    ) ?? null
  );
}

/**
 * Every regime whose filing is automated by a connector, with the system that
 * connector registers under. `apps/api`'s own test reads this and fails when
 * the set differs from what `registerFilingConnectors()` wires in,
 * which is the check the two tables never had.
 */
export function regimesWithFilingConnectors(): {
  slug: ComplianceRegimeSlug;
  system: RegistrationSystem;
}[] {
  return complianceRegimeSlugValues.flatMap((slug) => {
    const { automation } = REGIMES[slug];
    return automation.kind === "filing-connector"
      ? [{ slug, system: automation.system }]
      : [];
  });
}

/**
 * The regime whose page describes what this registration system files: the one
 * for the system's country and the obligation its category serves.
 *
 * The other direction of {@link regimesWithFilingConnectors}, and wider than
 * it. A system with no connector at all still has a regime with a claim
 * (Spain's SES.Hospedajes, Croatia's eVisitor), and so does a statistics
 * system, so a surface holding a system key can always ask what Vidimo says
 * about that filing.
 */
export function regimeForRegistrationSystem(
  system: string | null | undefined
): ComplianceRegimeSlug | null {
  const spec = system
    ? REGISTRATION_SYSTEMS[system as RegistrationSystem]
    : undefined;
  if (!spec) {
    return null;
  }
  return regimeForCountryObligation(
    spec.countryCode,
    OBLIGATION_BY_SYSTEM_CATEGORY[spec.category]
  );
}

/**
 * Whether Vidimo files this registration system's reports for a host today —
 * the question a host surface asks before offering to connect credentials to
 * it, because connecting one promises a filing.
 *
 * Each category is answered by the table that owns its liveness, and there are
 * two of them. A per-guest system is answered by its regime's claim, which
 * records the evidence it went live on (`provenBy`). A statistics
 * system is answered by the statistics catalog, because its liveness is per
 * destination and a region flips there, never here.
 *
 * So this is not the negation of "has a connector": Spain, Portugal and
 * Croatia have no connector at all and answer false for the stronger reason,
 * and ISTAT answers false while every region is still `planned`.
 *
 * It is answered where the property files from: a system not offered there
 * files nothing, and a statistics system answers for the destination its
 * municipality reports to, so a region going live opens setup there alone.
 */
export function registrationSystemFilesForHosts(
  system: RegistrationSystem,
  place: FilingMunicipality | null
): boolean {
  const config = systemConfigAt(system, place);
  if (!config) {
    return false;
  }
  if (REGISTRATION_SYSTEMS[system].category === "statistics") {
    const { region } = config;
    return region !== undefined && STATISTICS_REGIONS[region].status === "live";
  }
  return regimeFilesToAuthority(regimeForRegistrationSystem(system));
}

/**
 * Every registration system whose filings may actually go out, in catalog
 * order. The filing engine's default scope, the way `liveStatisticsRegions`
 * (statistics-filing.ts) is the statistics scheduler's: a system enters it
 * only once its regime's claim records the evidence it went live on.
 */
export function liveFilingSystems(): RegistrationSystem[] {
  return regimesWithFilingConnectors()
    .filter(({ slug }) => regimeFilesToAuthority(slug))
    .map(({ system }) => system);
}

/**
 * What one obligation actually costs a host in one country, and what Vidimo
 * does about it.
 *
 * {@link REGIMES} answers for the regimes Vidimo publishes a page for, and a
 * country's duties outrun its pages: Slovenia and Croatia both levy a tourist
 * tax and neither has a page for it, so `regimeForCountryObligation` answers
 * null where the duty plainly exists. That gap is why the outreach drafts
 * described each country's burden from memory, and why one of them described
 * Croatia's wrongly for months.
 *
 * `repeatsEnteredData` is the fact that went wrong, so it is the fact this
 * table exists to carry. A cold email's opening line names the reader's pain,
 * and the pain worth naming is working the same guest data twice. It is true
 * in Slovenia and Italy, where the host computes and remits the tax on their
 * own schedule, and false in Croatia, where eVisitor derives the charge from
 * the registrations already entered and issues the payment slips. An opener
 * that gets this wrong tells a host their own regime works in a way they know
 * it does not.
 *
 * Absent is unknown, never false: a market nobody has modelled gets no claim
 * from one that has, the way `packages/crm-core/src/contactability.ts` fails
 * closed. `draft.test.ts` reads this and fails when a template names an
 * obligation with no row here, which is what the English fallback was doing to
 * every country outside the three below.
 */
export interface ObligationBurden {
  /**
   * Whether discharging it costs the host a second pass over guest data they
   * have already entered, rather than the authority deriving it.
   */
  repeatsEnteredData: boolean;
  /**
   * The page whose cited copy these facts are read from.
   *
   * Its country must match, and a test enforces that. Its *obligation* need
   * not: a regime page documents the whole duty set a host in that market
   * carries, not only the one its slug is named for. Slovenia's tourist-tax
   * row cites `eturizem` for exactly that reason — the eTurizem page lists all
   * three Slovenian obligations with their deadlines, and Slovenia has no
   * tourist-tax page of its own to cite instead.
   */
  source: ComplianceRegimeSlug;
}

/**
 * Keyed by ISO 3166-1 alpha-2, then by obligation. Country is data here, and
 * the outer key is a plain string rather than a union so an unmodelled market
 * is a lookup miss rather than a type error nobody can satisfy.
 */
const OBLIGATION_BURDENS: Record<
  string,
  Partial<Record<RegimeObligation, ObligationBurden>>
> = {
  HR: {
    // The eVisitor page's own obligations: the check-out "is a separate step
    // with the same deadline", fined like a late check-in. Zakon o turističkoj
    // pristojbi art. 22(1) sets both 24-hour deadlines, and HTZ's user manual
    // §3.3.3 walks the host through performing the odjava.
    "guest-registration": { repeatsEnteredData: true, source: "evisitor" },
    // Croatia's tourist tax has NO row on purpose, and the omission is the
    // point rather than a gap left to fill.
    //
    // It is the one duty here whose answer depends on which operator is
    // asking. A private renter pays "an annual amount per bed ... whatever the
    // nights sold", which eVisitor derives and issues slips for — nothing is
    // worked twice. An agency or a hotel collects per night, which the same
    // page confirms when it says under-12s pay nothing and 12-to-18s pay half
    // "where it is charged per night" — and that is a second pass. The drafts
    // segment by size at CONSOLIDATION_UNITS_THRESHOLD and would ask this
    // table on both sides of that line.
    //
    // A boolean here would have to AND the two, which is exactly the country-
    // grain mistake this table exists to stop; the honest answer at this grain
    // is none, so no draft may claim it either way.
  },
  IT: {
    "guest-registration": {
      repeatsEnteredData: true,
      source: "alloggiati-web",
    },
    // The host collects and remits the tax and files the annual declaration,
    // except where the ledger's `it.tourist-tax.liable-party` makes a short
    // let's intermediary liable.
    "tourist-tax": {
      repeatsEnteredData: true,
      source: "imposta-di-soggiorno",
    },
  },
  SI: {
    "guest-registration": { repeatsEnteredData: true, source: "eturizem" },
    // The eTurizem page's own obligations: "You collect the tax on the
    // municipality's behalf and remit it to its account", by the 25th for the
    // previous month. Slovenia has no tourist-tax page, so this is the only
    // machine-readable record of it.
    "tourist-tax": { repeatsEnteredData: true, source: "eturizem" },
  },
};

/** The countries this table answers for, for the guards that iterate them. */
export function burdenedCountries(): string[] {
  return Object.keys(OBLIGATION_BURDENS);
}

/**
 * What an obligation costs a host in one country, or null where nobody has
 * modelled it. Ask before writing a sentence about a prospect's burden.
 */
export function obligationBurden(
  countryCode: string | null | undefined,
  obligation: RegimeObligation
): ObligationBurden | null {
  if (!countryCode) {
    return null;
  }
  return OBLIGATION_BURDENS[countryCode.toUpperCase()]?.[obligation] ?? null;
}
